// Seat every lever on its bar the way a real hood sits, measured from the bar
// artwork itself so it holds for any handlebar or integrated cockpit.
// Ratios come from the approved reference (Shimano hood on a taped bar), in
// bar diameters D: hood length (rear edge to front face) 4.7 D; hood rear on
// the bar top 1.6 D behind the front of the bend; rear-to-horn-peak line
// rising 24 degrees.
export const HOOD = { length: 4.7, back: 1.6, pitch: -24 * Math.PI / 180 };

// Lever artwork landmarks in natural pixels: R rear-top corner of the hood,
// H top of the front face, P horn peak.
const LEVERS = {
  '/resources/groupsets/ccache/sram-red-lever.png': { R: [3, 90], H: [283, 85], P: [222, 2] },
  '/resources/groupsets/ccache/sram-force-lever.png': { R: [1, 86], H: [269, 75], P: [208, 1] },
  '/resources/groupsets/ccache/shimano-dura-ace-lever.png': { R: [6, 84], H: [281, 33], P: [217, 3] },
  '/resources/groupsets/1manetacampyv4.png': { R: [143, 204], H: [289, 178], P: [263, 127] }
};

export function leverLandmarks(source = '') {
  const key = Object.keys(LEVERS).find(path => source.endsWith(path));
  return key ? LEVERS[key] : null;
}

const silhouettes = new Map();

// Alpha mask of a host image (bar or integrated frame) in its layout units.
export function silhouette(image, width, height) {
  const w = Math.round(width), h = Math.round(height);
  const key = `${image && image.src}|${w}x${h}`;
  if (silhouettes.has(key)) return silhouettes.get(key);
  let mask = null;
  try {
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(image, 0, 0, w, h);
    const px = ctx.getImageData(0, 0, w, h).data;
    const alpha = new Uint8Array(w * h);
    for (let i = 0; i < alpha.length; i++) alpha[i] = px[i * 4 + 3];
    mask = { w, h, alpha };
  } catch (e) {
    mask = null; // unreadable (cross-origin) artwork keeps its calibrated lever
  }
  if (silhouettes.size > 40) silhouettes.delete(silhouettes.keys().next().value);
  silhouettes.set(key, mask);
  return mask;
}

// Where the hood's rear edge sits on this bar, and the bar diameter there.
// `anchor` is the existing calibrated contact and only bounds the search;
// `roi` limits rows/columns on integrated frames so forks never count.
export function seatOnBar(mask, anchor, roi = null) {
  if (!mask || !anchor) return null;
  const { w, h, alpha } = mask;
  const op = (x, y) => x >= 0 && y >= 0 && x < w && y < h && alpha[(y | 0) * w + (x | 0)] > 128;
  const y0 = Math.max(0, Math.round(roi ? anchor.y - roi.up : 0));
  const y1 = Math.min(h - 1, Math.round(roi ? anchor.y + roi.down : h - 1));
  const x0 = Math.max(0, Math.round(roi ? anchor.x - roi.back : 0));
  // Front of the bend: the right-most bar pixels in the search band. Their
  // vertical span's middle is where the tube runs straight down.
  const outer = new Int32Array(y1 - y0 + 1).fill(-1);
  for (let y = y0; y <= y1; y++) {
    for (let x = w - 1; x > x0; x--) if (op(x, y)) { outer[y - y0] = x; break; }
  }
  let fx = -1;
  for (const x of outer) if (x > fx) fx = x;
  if (fx < 0) return null;
  let first = -1, last = -1;
  outer.forEach((x, i) => { if (x >= fx - 1) { if (first < 0) first = i; last = i; } });
  const front = { x: fx, y: y0 + Math.round((first + last) / 2) };
  // A horizontal cut there is the tube diameter; median of the rows nearby.
  const cut = y => { let x = outer[y - y0]; if (x < 0) return 0; const end = x; while (x > x0 && op(x - 1, y)) x--; return end - x + 1; };
  const cuts = [];
  for (let y = front.y - 2; y <= front.y + 2; y++) if (y >= y0 && y <= y1) cuts.push(cut(y));
  const D = Math.max(3, cuts.sort((a, b) => a - b)[cuts.length >> 1] || 0);
  // Bar top at a column: the first bar pixel from above, near the bend.
  const topAt = x => {
    for (let y = Math.max(0, Math.round(front.y - 6 * D)); y <= Math.min(h - 1, Math.round(front.y + D)); y++) {
      if (op(Math.round(x), y)) return y;
    }
    return null;
  };
  return { D, front, topAt };
}

// Lever layer fields that put its hood rear on `seat`. The hood keeps its
// calibrated real-world size (`baseLength`, pre-frame units) unless the bar is
// thin, then shrinks to the reference proportion (never below 70%).
// `hostScale` converts host layout units to the lever's pre-frame scale.
export function fitLever(seat, hostScale, landmarks, baseLength) {
  if (!seat || !landmarks || !(baseLength > 0)) return null;
  const [rx, ry] = landmarks.R, [hx, hy] = landmarks.H, [px, py] = landmarks.P;
  const art = Math.hypot(hx - rx, hy - ry);
  const length = Math.min(baseLength, Math.max(.7 * baseLength, HOOD.length * seat.D * hostScale));
  // Rear edge on the bar top, the reference's share of a hood length behind the bend.
  const x = seat.front.x - (HOOD.back / HOOD.length) * length / hostScale;
  const top = seat.topAt(x);
  if (top == null) return null;
  const peak = Math.atan2(py - ry, px - rx);
  return {
    anchor: { x, y: top + .05 * seat.D },
    origin: { x: rx, y: ry },
    scale: length / art,
    // Drawn with rotate(-rotation): the rear-to-peak line ends at HOOD.pitch.
    rotation: peak - HOOD.pitch
  };
}

// Hood length (rear edge to front face) of a calibrated lever layer, in the
// same pre-frame units as its scale.
export function calibratedHoodLength(landmarks, layer, image) {
  if (!landmarks || !image || !image.naturalWidth) return 0;
  const [rx, ry] = landmarks.R, [hx, hy] = landmarks.H;
  return Math.hypot(hx - rx, hy - ry) * (layer.width / image.naturalWidth) * layer.scale;
}
